/**
 * Settings ▸ Spaces — the tenant's horizontal slices (#695).
 *
 * A space partitions the workspace's content (specs, docs, rules…); content
 * grants are assigned per space. `open` spaces are reachable by everyone's
 * baseline persona; `restricted` spaces need an explicit per-space grant.
 * The default space is the guaranteed open home for tenant-wide content —
 * it stays open and cannot be archived (server-enforced, mirrored here).
 *
 * Admin-gated: non-admins see the list read-only (the server hides
 * restricted spaces they hold no grant in).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  Check,
  Globe,
  Lock,
  Pencil,
  Plus,
  ShieldPlus,
  X,
} from 'lucide-react';
import { LoadingState, Spinner } from '@systemdox/ui';
import { useAuthContext } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { problemDetail, tenantApi, type TenantBinding } from '@/services/tenantApi';
import { spacesApi, type Space, type SpaceVisibility } from '@/services/spacesApi';
import { presetBinding, toWriteBinding } from '@/lib/grants';
import { usePresets } from '@/lib/usePresets';
import { cn } from '@/lib/utils';

const selectClass =
  'rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm text-slate-700 dark:text-slate-300 focus:border-purple-500 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed';

const inputClass =
  'rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:border-purple-500 focus:outline-none';

function VisibilityBadge({ visibility }: { visibility: SpaceVisibility }) {
  const open = visibility === 'open';
  return (
    <span
      title={
        open
          ? 'Reachable by everyone’s baseline persona'
          : 'Only members with an explicit grant in this space can see its content'
      }
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        open
          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
          : 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
      )}
    >
      {open ? <Globe className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
      {open ? 'Open' : 'Restricted'}
    </span>
  );
}

export default function SpacesTab() {
  const { tenant, refreshTenants } = useTenant();
  const { user } = useAuthContext();
  const canManage = tenant?.role === 'owner' || tenant?.role === 'admin';
  const isOwner = tenant?.role === 'owner';
  const selfUserId = useMemo(() => {
    const id = user?.userId ?? '';
    return id && !id.startsWith('usr_') ? `usr_${id}` : id;
  }, [user?.userId]);

  const [spaces, setSpaces] = useState<Space[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // The catalogue the self-grant copies its scopes from. Null while loading
  // or after a failed read — the button is disabled and `presetsError`
  // shown; there is no built-in list to fall back to.
  const { presets, error: presetsError } = usePresets();

  const load = useCallback(async () => {
    try {
      setSpaces(await spacesApi.listSpaces());
      setError(null);
    } catch (err) {
      setError(problemDetail(err, 'Could not load spaces.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (space: Space, changes: Parameters<typeof spacesApi.updateSpace>[1]) => {
    if (busyId) return;
    setBusyId(space.spaceId);
    setError(null);
    try {
      const updated = await spacesApi.updateSpace(space.spaceId, changes);
      setSpaces((prev) => prev.map((s) => (s.spaceId === space.spaceId ? updated : s)));
    } catch (err) {
      setError(problemDetail(err, 'Could not update the space.'));
    } finally {
      setBusyId(null);
    }
  };

  // ── Owner break-glass self-grant (audited authz.self_grant) ──────────
  // The grantor cap is space-exact, so the FIRST binding in a space can
  // only come from an owner explicitly self-granting; after that they can
  // grant presets (and guest invites) to others there via Members.
  const selfGrant = async (space: Space) => {
    if (!tenant || !selfUserId || busyId || !presets) return;
    setBusyId(space.spaceId);
    setError(null);
    try {
      const members = await tenantApi.listMembers(tenant.tenantId);
      const self = members.find((m) => m.userId === selfUserId);
      const existing = (self?.bindings ?? []).filter((b) => b.space !== space.spaceId);
      // The PUT replaces the whole list, so every other binding rides along
      // in the write shape; one it cannot express refuses loudly here rather
      // than narrowing the owner's own access.
      let bindings: TenantBinding[];
      try {
        bindings = [
          ...existing.map((b) => toWriteBinding(b, presets)),
          presetBinding(space.spaceId, 'architect', presets),
        ];
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        return;
      }
      await tenantApi.setMemberBindings(tenant.tenantId, selfUserId, bindings);
      await refreshTenants(); // tenant.grants picks up the new space
    } catch (err) {
      setError(problemDetail(err, 'Could not grant yourself access.'));
    } finally {
      setBusyId(null);
    }
  };

  // ── Inline rename ────────────────────────────────────────────────────
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const submitRename = async (space: Space) => {
    const name = renameValue.trim();
    setRenaming(null);
    if (name && name !== space.name) await patch(space, { name });
  };

  // ── Create form ──────────────────────────────────────────────────────
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newVisibility, setNewVisibility] = useState<SpaceVisibility>('restricted');
  const [saving, setSaving] = useState(false);

  const createSpace = async () => {
    const name = newName.trim();
    if (!name || saving) return;
    setSaving(true);
    setError(null);
    try {
      const created = await spacesApi.createSpace({ name, visibility: newVisibility });
      setSpaces((prev) => [...prev, created]);
      setCreating(false);
      setNewName('');
      setNewVisibility('restricted');
    } catch (err) {
      setError(problemDetail(err, 'Could not create the space.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <LoadingState size="lg" label="Loading spaces…" />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
          Spaces {`(${spaces.length})`}
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
          Spaces partition this workspace's content; personas are granted per space.
          <span className="font-medium"> Open</span> spaces are reachable by everyone's baseline
          persona; <span className="font-medium">restricted</span> spaces need an explicit grant,
          assigned in Members.
        </p>
      </div>

      {error && (
        <p className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </p>
      )}
      {presetsError && (
        <p className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="w-4 h-4 shrink-0" /> Access presets unavailable — {presetsError}
        </p>
      )}

      <div className="divide-y divide-slate-100 dark:divide-slate-800 rounded-lg border border-slate-200 dark:border-slate-800">
        {spaces.map((space) => {
          const busy = busyId === space.spaceId;
          const archived = Boolean(space.archivedAt);
          const editable = canManage && !space.isDefault;
          return (
            <div
              key={space.spaceId}
              className={cn('flex items-center gap-3 px-4 py-3', archived && 'opacity-60')}
            >
              <div className="min-w-0 flex-1">
                {renaming === space.spaceId ? (
                  <span className="flex items-center gap-2">
                    <input
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') submitRename(space);
                        if (e.key === 'Escape') setRenaming(null);
                      }}
                      autoFocus
                      maxLength={100}
                      className={cn(inputClass, 'py-1 max-w-xs')}
                    />
                    <button
                      type="button"
                      onClick={() => submitRename(space)}
                      className="text-emerald-600 hover:text-emerald-700"
                      title="Save name"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setRenaming(null)}
                      className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                      title="Cancel"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </span>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">
                      {space.name}
                    </span>
                    {space.isDefault && (
                      <span
                        className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs text-slate-500 dark:text-slate-400"
                        title="The guaranteed open home for tenant-wide content — always open, never archived"
                      >
                        Default
                      </span>
                    )}
                    {archived && (
                      <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs text-slate-500 dark:text-slate-400">
                        Archived
                      </span>
                    )}
                    {editable && !archived && (
                      <button
                        type="button"
                        onClick={() => {
                          setRenaming(space.spaceId);
                          setRenameValue(space.name);
                        }}
                        disabled={Boolean(busyId)}
                        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                        title="Rename space"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {busy && <Spinner size="sm" />}

              {isOwner && !archived && !space.isDefault && !(space.spaceId in (tenant?.grants ?? {})) && (
                <button
                  type="button"
                  onClick={() => selfGrant(space)}
                  disabled={Boolean(busyId) || !presets}
                  title="You hold no grant in this space. Self-granting is audited (authz.self_grant) and lets you then assign personas and guest invites here."
                  className="flex items-center gap-1 rounded-md border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <ShieldPlus className="w-3.5 h-3.5" /> Grant myself access
                </button>
              )}

              {editable && !archived ? (
                <select
                  value={space.visibility}
                  onChange={(e) =>
                    patch(space, { visibility: e.target.value as SpaceVisibility })
                  }
                  disabled={Boolean(busyId)}
                  className={selectClass}
                  title="Who can reach this space's content"
                >
                  <option value="restricted">Restricted</option>
                  <option value="open">Open</option>
                </select>
              ) : (
                <VisibilityBadge visibility={space.visibility} />
              )}

              {editable && (
                <button
                  type="button"
                  onClick={() => patch(space, { archived: !archived })}
                  disabled={Boolean(busyId)}
                  title={archived ? 'Restore space' : 'Archive space (content becomes unreachable)'}
                  className={cn(
                    'rounded-md p-1.5 text-slate-400',
                    archived
                      ? 'hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30'
                      : 'hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30',
                  )}
                >
                  {archived ? (
                    <ArchiveRestore className="w-4 h-4" />
                  ) : (
                    <Archive className="w-4 h-4" />
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {canManage &&
        (creating ? (
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') createSpace();
                if (e.key === 'Escape') setCreating(false);
              }}
              autoFocus
              maxLength={100}
              placeholder="Space name (e.g. Payments, Platform, Client X)"
              className={cn(inputClass, 'w-full max-w-sm')}
              disabled={saving}
            />
            <select
              value={newVisibility}
              onChange={(e) => setNewVisibility(e.target.value as SpaceVisibility)}
              disabled={saving}
              className={selectClass}
              title="Restricted (default): only explicitly granted members reach it"
            >
              <option value="restricted">Restricted</option>
              <option value="open">Open</option>
            </select>
            <button
              type="button"
              onClick={createSpace}
              disabled={saving || !newName.trim()}
              className={cn(
                'flex items-center gap-2 rounded-md bg-purple-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50',
              )}
            >
              {saving ? <Spinner size="sm" tone="current" /> : <Plus className="w-4 h-4" />}
              Create space
            </button>
            <button
              type="button"
              onClick={() => setCreating(false)}
              disabled={saving}
              className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="flex items-center gap-1 rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <Plus className="w-3.5 h-3.5" /> New space
          </button>
        ))}
    </div>
  );
}
